/* eslint-disable @typescript-eslint/naming-convention */ // Base API fields are snake_case
import { useEffect, useMemo, useState } from 'react';
import { BilberryReservationPerson, PaymentInfo } from '@repo/types';
import { createReservation } from '@repo/widget-utils/services/fetchers/product';
import { guardIsValidPaymentGateway } from '@repo/widget-utils/reservation-helper';
import { useNativeMemberCardProducts } from '@repo/widget-utils/services/hooks/native-membership';
import { paymentInfoFromBooking, shouldSkipPayment } from './nativeReservationHelpers';
import { configurationAtom } from '@repo/widget-utils/widgetsConfiguration';
import { tzdate } from '@repo/tzdate';
import { useNativeMemberContext } from './NativeMemberContext';

type Step = 'select' | 'pay' | 'done';

/**
 * Smart layer for the member-card buy widget. Owns product selection, the optional deferred
 * start date, the member's contact details (pre-filled; phone is collected only when the
 * profile lacks one), and the reserve → payment routing.
 */
export function useMemberCardPurchase(preselectedProductId?: string, allowedProductIds?: string[]) {
    const { products: allProducts, isLoading: isLoadingProducts } = useNativeMemberCardProducts();
    const { isNative, isLoggedIn, member, token, openLoginModal, logout } =
        useNativeMemberContext();

    // Optional whitelist (from the `member-card-product-ids` attribute): show only those products,
    // ordered by the given list. Empty/absent → all sellable products (the default).
    const products = useMemo(() => {
        if (!allowedProductIds || allowedProductIds.length === 0) return allProducts;
        const order = new Map(allowedProductIds.map((id, index) => [id, index] as const));
        return allProducts
            .filter((product) => order.has(product.id))
            .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }, [allProducts, allowedProductIds]);

    const [selectedProductId, setSelectedProductId] = useState(preselectedProductId ?? '');
    const [startDate, setStartDate] = useState(() => tzdate().format('YYYY-MM-DD')); // yyyy-mm-dd; defaults to today
    const [phone, setPhone] = useState('');
    const [step, setStep] = useState<Step>('select');
    const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
    const [reserving, setReserving] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Pre-fill the phone from the member profile (the rare missing-phone case shows a field).
    useEffect(() => {
        if (member?.phone) setPhone(member.phone);
    }, [member?.phone]);

    // Auto-select when there is a single product (and nothing was preselected).
    useEffect(() => {
        if (!selectedProductId && products.length === 1) setSelectedProductId(products[0].id);
    }, [products, selectedProductId]);

    const selectedProduct = useMemo(
        () => products.find((p) => p.id === selectedProductId) ?? null,
        [products, selectedProductId],
    );

    // Only collect a phone when a logged-in member's profile lacks one. Logged out, there is
    // no member to source contact info from and the CTA is "log in" — so don't show the field
    // (it would contradict the "we will fill out your contact info for you" copy). `isLoggedIn`
    // implies `member` is present (see NativeMemberContext), so `!member.phone` is the real gap.
    const needsPhone = isLoggedIn && !member?.phone;
    const resolvedPhone = (member?.phone ?? phone).trim();
    const canSubmit =
        Boolean(selectedProduct) && !reserving && (!needsPhone || resolvedPhone !== '');

    const submit = async () => {
        // Logged-out: open the login modal; selection is preserved so the member can continue.
        if (!isLoggedIn || !token || !member) {
            openLoginModal();
            return;
        }
        if (!selectedProduct || reserving || !resolvedPhone) return;

        setHasError(false);
        setReserving(true);
        try {
            const config = configurationAtom.subject.value;
            const reservation: BilberryReservationPerson = {
                name: `${member.first_name} ${member.last_name}`.trim(),
                email: member.email,
                phone: resolvedPhone,
                country: member.country ?? '',
                tours: [],
                packages: [],
                projects: [],
                extras: [],
                external_data: [],
                url: window.location.toString(),
                termsUrl: config.termsUrl,
                giftcards: [],
                rebook_from_invoice_ids: [],
                member_cards: [
                    {
                        member_card_product_id: selectedProduct.id,
                        key: 'mc-0',
                        // Only send a start date for products that allow a deferred start; Base 422s a
                        // start_date on products where allowsDeferredStart is false.
                        start_date: selectedProduct.allowsDeferredStart ? startDate || null : null,
                    },
                ],
            };

            const booking = await createReservation(reservation, undefined, 'person', token);
            guardIsValidPaymentGateway(booking);
            const info = paymentInfoFromBooking(booking, reservation);

            if (shouldSkipPayment(info, booking)) {
                setStep('done');
            } else {
                setPaymentInfo(info);
                setStep('pay');
            }
        } catch {
            setHasError(true);
        } finally {
            setReserving(false);
        }
    };

    const onPaymentCompleted = () => setStep('done');
    const onPaymentCancelled = () => {
        setPaymentInfo(null);
        setStep('select');
    };
    const reset = () => {
        setStep('select');
        setPaymentInfo(null);
        setHasError(false);
    };

    return {
        isNative,
        isLoggedIn,
        member,
        logout,
        products,
        isLoadingProducts,
        selectedProduct,
        selectedProductId,
        setSelectedProductId,
        startDate,
        setStartDate,
        needsPhone,
        phone,
        setPhone,
        step,
        paymentInfo,
        reserving,
        hasError,
        canSubmit,
        submit,
        onPaymentCompleted,
        onPaymentCancelled,
        reset,
    };
}
